/*
  UserUpdateSkillButton
*/

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import autoBind from 'react-autobind';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import * as schemas from 'store/common/schemas';
import * as entityActions from 'store/entities/actions';
import { normalize } from 'normalizr';

import * as flashActions from 'store/features/common/flash/actions';
import messages from './messages';
import Button from 'ui/common/button';

import * as actionTypes from 'store/graphql/UserUpdateSkillButton/action-types';
import { skillLevelIds } from 'resources/skills/levels';
import { selectIsUpdatingUserSkill } from 'store/graphql/UserUpdateSkillButton/selectors';

import { compose, bindActionCreators } from 'redux';
import injectReducer from 'utils/injectReducer';
import UserUpdateSkillButtonReducer from 'store/graphql/UserUpdateSkillButton/reducer';

class UserUpdateSkillButton extends Component {
  static propTypes = {
    level: PropTypes.oneOf([
      skillLevelIds.beginner,
      skillLevelIds.intermediate,
      skillLevelIds.expert,
    ]),
    skillName: PropTypes.string,
    isHighlighted: PropTypes.bool,
    color: PropTypes.string,
    isReady: PropTypes.bool,
    save: PropTypes.bool,
    closeModalFn: PropTypes.func,
  };

  constructor(props) {
    super(props);
    autoBind(this);
  }

  async onUpdate() {
    const {
      client,
      skillName,
      isHighlighted,
      level,
      save,
      userUpdateSkillStart,
      userUpdateSkillSuccess,
      userUpdateSkillFailure,
      mergeEntities,
      addMessage,
      intl: { formatMessage },
      closeModalFn,
    } = this.props;
    userUpdateSkillStart();

    // isHighlighted is always a boolean, no null, 0, etc. only true or false.
    let isCurrentHighlighted = save ? isHighlighted : !isHighlighted;

    try {
      const response = await client.mutate({
        mutation: gql`
          mutation skillUpdate($name: String!, $level: levels_type!, $isHighlighted: Boolean!) {
            skillUpdate(name: $name, level: $level, isHighlighted: $isHighlighted) {
              skillUpdated
              skill {
                level
                _id
                isDeleted
                isHighlighted
                validations {
                  validated
                  id
                  level
                  type
                  _id
                }
                skill {
                  _id
                  name
                  category
                }
              }
            }
          }
        `,
        variables: {
          name: skillName,
          level: skillLevelIds[level],
          isHighlighted: isCurrentHighlighted,
        },
      });

      const { data, errors } = response;

      if (errors && errors[0].message) {
        userUpdateSkillFailure(errors[0].message);
      } else if (data && data.skillUpdate) {
        const { skillUpdated, skill } = data.skillUpdate;

        skill.id = skill._id;
        skill.skill.id = skill.skill._id;
        delete skill._id;
        delete skill.skill._id;

        const schema = schemas.userSkill;
        const { entities, result } = normalize(skill, schema);

        mergeEntities(entities);
        userUpdateSkillSuccess({
          skillUpdated,
          skill,
          userSkillId: result,
        });
        closeModalFn();
        addMessage({
          id: 'graphql.user.update.skill.success',
          kind: 'success',
          content: formatMessage(messages.success, { skillName }),
        });
      }
    } catch (error) {
      userUpdateSkillFailure(error);
    }
  }

  render() {
    const {
      skillName,
      skillId,
      color,
      isReady,
      children,
      updatingUserSkill,
      ...other
    } = this.props;

    return (
      <Button
        loading={updatingUserSkill}
        iconPosition="suffix"
        onClick={this.onUpdate}
        color={color}
        isReady={isReady}
        {...other}
      >
        {children}
      </Button>
    );
  }
}

function mapStateToProps(state, props) {
  return {
    updatingUserSkill: selectIsUpdatingUserSkill(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    userUpdateSkillStart: () => dispatch({ type: actionTypes.UPDATE_USER_SKILL.START }),
    userUpdateSkillSuccess: ({ skillUpdated, skill }) =>
      dispatch({
        type: actionTypes.UPDATE_USER_SKILL.SUCCESS,
        payload: {
          skillUpdated,
          skill,
        },
      }),
    userUpdateSkillFailure: error =>
      dispatch({ type: actionTypes.UPDATE_USER_SKILL.FAILURE, payload: { error } }),
    mergeEntities: bindActionCreators(entityActions.mergeEntities, dispatch),
    addMessage: bindActionCreators(flashActions.addMessage, dispatch),
  };
}

UserUpdateSkillButton.defaultProps = {
  closeModalFn: () => {},
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withReducer = injectReducer({
  key: 'UserUpdateSkillButton',
  reducer: UserUpdateSkillButtonReducer,
});

export default compose(withReducer, withConnect)(injectIntl(withApollo(UserUpdateSkillButton)));
